import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';

const { 
  FiUpload, FiSettings, FiZap, FiDownload, FiImage, FiLayers, 
  FiShield, FiClock, FiCheckCircle, FiMonitor, FiSmartphone, FiHelpCircle 
} = FiIcons;

function HowItWorks() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const mainSteps = [
    {
      step: "1",
      icon: FiUpload,
      title: "Upload Your Images",
      description: "Select images from your device using our drag-and-drop interface or file browser. We support all major formats including JPEG, PNG, WebP, and GIF.",
      details: [
        "Drag & drop multiple files",
        "Browse and select files",
        "Support for all major formats",
        "Up to 50MB per image",
        "Batch upload available"
      ]
    },
    {
      step: "2",
      icon: FiSettings,
      title: "Configure Settings",
      description: "Choose your processing options including dimensions, quality levels, output formats, and other advanced settings based on your needs.",
      details: [
        "Custom dimensions or presets",
        "Quality control sliders",
        "Format conversion options",
        "Aspect ratio preservation",
        "Batch settings application"
      ]
    },
    {
      step: "3",
      icon: FiZap,
      title: "Process Images",
      description: "Our advanced algorithms process your images locally in your browser. Watch real-time progress and preview results before downloading.",
      details: [
        "Local browser processing",
        "Real-time progress tracking",
        "Live preview available",
        "Error handling & recovery",
        "Pause/resume capability"
      ]
    },
    {
      step: "4",
      icon: FiDownload,
      title: "Download Results",
      description: "Download your processed images individually or as a ZIP archive. No watermarks, no registration required, and your privacy is protected.",
      details: [
        "Individual downloads",
        "ZIP archive option",
        "No watermarks added",
        "Instant availability",
        "Original filenames preserved"
      ]
    }
  ];

  const toolWorkflows = [
    {
      tool: "Image Resizer",
      icon: FiImage,
      color: "blue",
      steps: [
        "Upload single image (up to 50MB)",
        "Set custom dimensions or choose preset",
        "Preview resized image in real-time",
        "Download high-quality result instantly"
      ]
    },
    {
      tool: "Image Compressor",
      icon: FiZap,
      color: "green",
      steps: [
        "Upload image for compression",
        "Adjust quality slider (10-100%)",
        "See file size reduction preview",
        "Download optimized image"
      ]
    },
    {
      tool: "Batch Processor",
      icon: FiLayers,
      color: "purple",
      steps: [
        "Select multiple images (up to 100)",
        "Apply same settings to all images",
        "Monitor batch processing progress",
        "Download all results as ZIP"
      ]
    }
  ];

  const technicalDetails = [
    {
      icon: FiShield,
      title: "Privacy & Security",
      description: "All processing happens locally in your browser using HTML5 Canvas API and JavaScript. Your images never leave your device."
    },
    {
      icon: FiClock,
      title: "Performance",
      description: "Optimized algorithms ensure fast processing times. Most images process in under 5 seconds, with batch operations scaling efficiently."
    },
    {
      icon: FiCheckCircle,
      title: "Quality Assurance",
      description: "Advanced resampling algorithms maintain image quality during resizing. Smart compression preserves visual fidelity."
    },
    {
      icon: FiMonitor,
      title: "Browser Compatibility",
      description: "Works on all modern browsers including Chrome, Firefox, Safari, and Edge. No plugins or extensions required."
    }
  ];

  const supportedFormats = [
    {
      format: "JPEG/JPG",
      description: "Most common web format, excellent for photos",
      features: ["Lossy compression", "Small file sizes", "Wide compatibility"]
    },
    {
      format: "PNG",
      description: "Perfect for graphics with transparency",
      features: ["Lossless compression", "Transparency support", "Sharp edges"]
    },
    {
      format: "WebP",
      description: "Modern format with superior compression",
      features: ["Smaller than JPEG", "Transparency support", "Modern browsers"]
    },
    {
      format: "GIF",
      description: "Animation support and legacy compatibility",
      features: ["Animation support", "Limited colors", "Wide compatibility"]
    }
  ];

  return (
    <>
      <SEOHead
        title="How It Works - Step-by-Step Guide | ResizeImageFast"
        description="Learn how to use our free image processing tools. Step-by-step guide for resizing, compressing, and batch processing images. Simple, secure, and completely free."
        keywords="how to resize images, how to compress images, image processing guide, batch image processing, free image tools tutorial, online image editor guide"
        url="/how-it-works"
        image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=630&fit=crop"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-emerald-100">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <div className="bg-green-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiHelpCircle} className="text-white text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                How It Works
                <span className="block text-green-600">Simple & Secure</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Learn how to process your images with our professional tools. 
                Follow our step-by-step guide for resizing, compressing, and batch processing images.
              </p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto">
                {[
                  { number: "4", label: "Simple Steps" },
                  { number: "100%", label: "Browser Based" },
                  { number: "0", label: "Server Uploads" },
                  { number: "∞", label: "Usage Limit" }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold text-green-600">{stat.number}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Steps Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                4 Simple Steps to Process Your Images
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our streamlined process makes image processing fast, secure, and user-friendly
              </p>
            </motion.div>

            <div className="space-y-16">
              {mainSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}
                >
                  {/* Step Content */}
                  <div className="flex-1">
                    <div className="flex items-center mb-6">
                      <div className="bg-green-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mr-4">
                        {step.step}
                      </div>
                      <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center">
                        <SafeIcon icon={step.icon} className="text-green-600 text-xl" />
                      </div>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-lg text-gray-600 mb-6">
                      {step.description}
                    </p>
                    <ul className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="flex items-center text-gray-700">
                          <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Step Visual */}
                  <div className="flex-1">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 h-64 flex items-center justify-center">
                      <SafeIcon icon={step.icon} className="text-6xl text-gray-400" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Tool-Specific Workflows */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Specific Tool Workflows
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Each tool has its own optimized workflow for the best results
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-3 gap-8">
              {toolWorkflows.map((workflow, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white rounded-2xl p-8 shadow-lg"
                >
                  <div className={`bg-${workflow.color}-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6`}>
                    <SafeIcon icon={workflow.icon} className={`text-${workflow.color}-600 text-2xl`} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {workflow.tool}
                  </h3>
                  <ol className="space-y-3">
                    {workflow.steps.map((step, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className={`bg-${workflow.color}-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0`}>
                          {idx + 1}
                        </span>
                        <span className="text-gray-700">{step}</span>
                      </li>
                    ))}
                  </ol>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Details */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Technical Details
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Understanding how our tools work behind the scenes
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {technicalDetails.map((detail, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-2xl p-8"
                >
                  <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={detail.icon} className="text-blue-600 text-xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {detail.title}
                  </h3>
                  <p className="text-gray-600">
                    {detail.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Supported Formats */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Supported Image Formats
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We support all major image formats for maximum compatibility
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {supportedFormats.map((format, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 text-center shadow-lg"
                >
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {format.format}
                  </div>
                  <p className="text-gray-600 mb-4">
                    {format.description}
                  </p>
                  <ul className="space-y-1">
                    {format.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-gray-500">
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
            </motion.div>

            <div className="space-y-8">
              {[
                {
                  question: "Do I need to install any software?",
                  answer: "No! Our tools work entirely in your web browser. No downloads, installations, or plugins required. Just visit our website and start processing images immediately."
                },
                {
                  question: "How long does image processing take?",
                  answer: "Processing time depends on image size and your device's performance. Most single images process in under 5 seconds. Batch processing of 50 images typically takes 2-5 minutes."
                },
                {
                  question: "What happens to my images during processing?",
                  answer: "All processing happens locally in your browser using JavaScript and HTML5 Canvas. Your images never leave your device or get uploaded to our servers."
                },
                {
                  question: "Can I process images on mobile devices?",
                  answer: "Yes! Our tools are fully responsive and work on smartphones and tablets. The interface adapts to smaller screens while maintaining full functionality."
                },
                {
                  question: "Is there a limit to how many images I can process?",
                  answer: "No usage limits! You can process unlimited images. For batch processing, we recommend groups of 50 images or fewer for optimal performance."
                },
                {
                  question: "What if processing fails or gets interrupted?",
                  answer: "Our tools include error handling and recovery features. If processing fails, you can retry individual images or remove problematic files from your batch."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-lg p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-green-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-xl text-green-100 mb-8">
                Now that you know how it works, try our tools and see the results for yourself!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <SafeIcon icon={FiImage} className="mr-2" />
                    Start Resizing Images
                  </motion.button>
                </Link>
                <Link to="/compress">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-800 transition-colors flex items-center"
                  >
                    <SafeIcon icon={FiZap} className="mr-2" />
                    Start Compressing Images
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default HowItWorks;